import { INLINE_EDIT_LINK, InlineEditCard } from "@rosc/ui/v32/inline-edit";
import type { JSX } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { SentenceHit } from "@/lib/sentenceAtPoint";
import { cn } from "@/lib/utils";

// Click-to-edit one sentence (clinician request 04/11): a click on the note
// opens this small editor with only that sentence (a list item: its line),
// placed under it like the review card; on phones a bottom sheet. Enter or
// Save applies, Escape or Cancel closes, Shift+Enter adds a line; saved
// empty, the sentence is removed. Remove (clinician request 05/11) takes the
// sentence, or a list item's whole line, out in one click, with Undo.
// "Edit whole note" opens the full editor.

const CARD_WIDTH = 440;
const GAP = 8;
const MARGIN = 16;
const HIGHLIGHT = "rosc-note-edit";

type HighlightRegistry = {
  set: (name: string, value: unknown) => void;
  delete: (name: string) => void;
};
const highlights = (): HighlightRegistry | null => {
  const css = (globalThis as { CSS?: { highlights?: HighlightRegistry } }).CSS;
  return typeof (globalThis as { Highlight?: unknown }).Highlight ===
    "function" && css?.highlights
    ? css.highlights
    : null;
};

interface Props {
  hit: SentenceHit;
  phone: boolean;
  // Saves the edited text (empty: removes the sentence); resolves true when
  // saved, and the editor closes.
  onSave: (value: string) => Promise<boolean>;
  // Removes the sentence (a list item: its whole line); resolves true when
  // saved, and the editor closes. Without it there is no Remove button.
  onRemove?: () => Promise<boolean>;
  onClose: () => void;
  onEditWholeNote: () => void;
}

export const SentenceEditor = ({
  hit,
  phone,
  onSave,
  onRemove,
  onClose,
  onEditWholeNote,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const card = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState(hit.text);
  const [busyAction, setBusyAction] = useState<"save" | "remove" | null>(null);
  const busy = busyAction !== null;
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const dirty = value !== hit.text;
  const line = hit.unit.line.kind !== "paragraph";

  // The sentence stays marked while it is edited.
  useEffect(() => {
    const registry = highlights();
    if (!registry || !hit.range) return;
    const Highlight = (
      globalThis as unknown as { Highlight: new (range: Range) => unknown }
    ).Highlight;
    registry.set(HIGHLIGHT, new Highlight(hit.range));
    return () => registry.delete(HIGHLIGHT);
  }, [hit.range]);

  // Phones: the sentence scrolls to the top, above the sheet.
  useEffect(() => {
    if (!phone || !hit.range) return;
    const element =
      hit.range.startContainer.parentElement?.closest<HTMLElement>(
        "p, li, h1, h2, h3, h4, h5, h6",
      );
    if (!element) return;
    element.style.scrollMarginTop = "88px";
    element.scrollIntoView?.({ block: "start", behavior: "smooth" });
  }, [hit.range, phone]);

  // Desktop: under the sentence (above it when there is no room), kept on
  // screen while the note scrolls.
  useLayoutEffect(() => {
    if (phone) return;
    let frame = 0;
    const place = (): void => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const element = card.current;
        if (!element) return;
        const rects =
          hit.range && typeof hit.range.getClientRects === "function"
            ? Array.from(hit.range.getClientRects())
            : [];
        const first = rects[0];
        const last = rects[rects.length - 1];
        if (!first || !last) {
          setPosition({ top: MARGIN * 6, left: MARGIN });
          return;
        }
        const width = Math.min(CARD_WIDTH, window.innerWidth - 2 * MARGIN);
        const height = element.offsetHeight;
        const left = Math.min(
          // The sentence's leftmost line (a sentence that starts mid-line
          // and wraps lines up with the text's edge).
          Math.max(MARGIN, Math.min(...rects.map((rect) => rect.left)) - 12),
          window.innerWidth - width - MARGIN,
        );
        const below = last.bottom + GAP;
        const top =
          below + height <= window.innerHeight - MARGIN ||
          first.top - GAP - height < MARGIN
            ? below
            : first.top - GAP - height;
        setPosition((current) =>
          current?.top === top && current.left === left
            ? current
            : { top, left },
        );
      });
    };
    place();
    window.addEventListener("scroll", place, { capture: true, passive: true });
    window.addEventListener("resize", place);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", place, { capture: true });
      window.removeEventListener("resize", place);
    };
  }, [hit.range, phone]);

  // A press outside closes it, unless the text was changed (Cancel or
  // Escape discard a change on purpose).
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Node) || busy || dirty) return;
      if (card.current?.contains(target)) return;
      closeRef.current();
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [busy, dirty]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && !busy) closeRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy]);

  const save = async (): Promise<void> => {
    if (!dirty) {
      onClose();
      return;
    }
    await run("save", () => onSave(value));
  };

  // A failed save keeps the editor open with the text; the save shows its
  // own error.
  const run = async (
    action: "save" | "remove",
    task: () => Promise<boolean>,
  ): Promise<void> => {
    setBusyAction(action);
    let saved = false;
    try {
      saved = await task();
    } catch {
      saved = false;
    }
    setBusyAction(null);
    if (saved) onClose();
  };

  return (
    <>
      <style>{`::highlight(${HIGHLIGHT}){background-color:var(--v3-brand-tint-2);color:inherit}`}</style>
      <InlineEditCard
        ref={card}
        busy={busy}
        busyAction={busyAction ?? undefined}
        cancelLabel={t("v3.workspace.sentence.cancel")}
        className={cn(
          !phone && "fixed",
          // Not yet placed: transparent, not hidden, so its text box can
          // take focus as it opens.
          !phone && !position && "opacity-0",
          phone
            ? "animate-in fade-in-0 slide-in-from-bottom-8 duration-200"
            : "animate-in fade-in-0 zoom-in-95 duration-150",
        )}
        hint={t("v3.workspace.sentence.hint")}
        label={t("v3.workspace.sentence.label")}
        layout={phone ? "sheet" : "card"}
        removeLabel={t("v3.workspace.sentence.remove")}
        saveLabel={t("v3.workspace.sentence.save")}
        secondary={
          <button
            className={INLINE_EDIT_LINK}
            data-testid="sentence-editor-whole"
            disabled={busy}
            type="button"
            onClick={onEditWholeNote}
          >
            {t("v3.workspace.sentence.editWhole")}
          </button>
        }
        style={
          phone
            ? undefined
            : {
                top: position?.top ?? 0,
                left: position?.left ?? 0,
                width: `min(${CARD_WIDTH}px, calc(100vw - ${2 * MARGIN}px))`,
              }
        }
        title={
          line
            ? t("v3.workspace.sentence.titleLine")
            : t("v3.workspace.sentence.titleSentence")
        }
        value={value}
        removeActionAria={
          line
            ? t("v3.workspace.sentence.removeLineAria")
            : t("v3.workspace.sentence.removeSentenceAria")
        }
        onCancel={onClose}
        onRemove={onRemove ? () => void run("remove", onRemove) : undefined}
        onSave={() => void save()}
        onValueChange={setValue}
      />
    </>
  );
};
